// client/src/components/pages/LexiStar/Commissioning/AccuracyReport.jsx
//
// Steps 3 and 4 of commissioning (LS-09 §5.20): the daily comparison as it
// builds, and the accuracy report — the five measures against their floors,
// then per truck and per lane, then every miss with its cause (geofence too
// small, device gap, unresolved registration, …) and a link to fix it.
// Verdicts pair a glyph with words (✔ Meets · ✖ Below floor · — Not assessed).

import React from 'react';
import { Link } from 'react-router-dom';
import { cardClasses } from '../StatusBoard/atoms';
import { fmtDateTime, fmtNum } from '../StatusBoard/format';
import {
  METRICS,
  metricRows,
  metricPass,
  metricText,
  floorText,
  missSummary,
  missCauseLabel,
  MISS_CAUSES,
  RECORD_KINDS,
  fmtRatio,
} from './commissioningLogic';

export function Verdict({ pass }) {
  if (pass === true)
    return (
      <span
        className="inline-flex items-center gap-1 text-xs font-semibold text-slate-900"
        data-pass="true"
      >
        <span aria-hidden="true">✔</span> Meets
      </span>
    );
  if (pass === false)
    return (
      <span
        className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs font-semibold bg-surface text-red-700 ring-1 ring-inset ring-red-400"
        data-pass="false"
      >
        <span aria-hidden="true">✖</span> Below floor
      </span>
    );
  return (
    <span className="inline-flex items-center gap-1 text-xs text-slate-500" data-pass="na">
      <span aria-hidden="true">—</span> Not assessed
    </span>
  );
}

export function MetricsTable({ report, floors }) {
  const rows = metricRows(report, floors);
  return (
    <table className="w-full text-sm" aria-label="Accuracy against floors">
      <thead>
        <tr className="text-left text-[11px] uppercase tracking-wider text-slate-500 border-b border-slate-200">
          <th className="py-2 pr-3 font-semibold">Measure</th>
          <th className="py-2 pr-3 font-semibold">Result</th>
          <th className="py-2 pr-3 font-semibold">Floor</th>
          <th className="py-2 font-semibold">Verdict</th>
        </tr>
      </thead>
      <tbody className="divide-y divide-slate-100">
        {rows.map((r) => (
          <tr key={r.key}>
            <td className="py-2 pr-3">
              <span className="font-semibold text-slate-900">{r.label}</span>
              <span className="block text-xs text-slate-500">{r.what}</span>
            </td>
            <td className="py-2 pr-3 tabular-nums text-slate-900">{r.text}</td>
            <td className="py-2 pr-3 tabular-nums text-slate-700">{floorText(r, r.floor)}</td>
            <td className="py-2">
              <Verdict pass={r.pass} />
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function BreakdownTable({ title, rows, labelOf, floors }) {
  if (!rows?.length) return null;
  return (
    <section className={`${cardClasses} p-4 overflow-x-auto`} aria-label={title}>
      <h3 className="text-sm font-bold text-slate-900 mb-2">{title}</h3>
      <table className="w-full text-xs min-w-[640px]">
        <thead>
          <tr className="text-left text-[11px] uppercase tracking-wider text-slate-500 border-b border-slate-200">
            <th className="py-1.5 pr-3 font-semibold">
              {title === 'Per truck' ? 'Truck' : 'Lane'}
            </th>
            {METRICS.map((m) => (
              <th key={m.key} className="py-1.5 pr-3 font-semibold">
                {m.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {rows.map((row, i) => (
            <tr key={row.asset_key || row.lane_id || i}>
              <td className="py-1.5 pr-3 font-medium text-slate-900 whitespace-nowrap">
                {labelOf(row)}
              </td>
              {METRICS.map((meta) => {
                const m = row[meta.key] || row.metrics?.[meta.key] || null;
                const pass = metricPass(m, meta, floors);
                return (
                  <td
                    key={meta.key}
                    className={`py-1.5 pr-3 tabular-nums ${pass === false ? 'text-red-700 font-semibold' : 'text-slate-700'}`}
                  >
                    {pass === false && <span aria-label="below floor">✖ </span>}
                    {m ? metricText(m, meta).split(' ·')[0].split(' over')[0] : '—'}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  );
}

const FixLink = ({ href, cause }) => {
  const text = MISS_CAUSES[cause]?.fix || 'Fix';
  if (!href) return <span className="text-slate-400">—</span>;
  return href.startsWith('/') ? (
    <Link to={href} className="text-primary-700 hover:underline whitespace-nowrap">
      {text} →
    </Link>
  ) : (
    <a href={href} className="text-primary-700 hover:underline whitespace-nowrap">
      {text} →
    </a>
  );
};

export function MissesTable({ misses }) {
  const summary = missSummary(misses);
  if (!misses?.length)
    return <p className="text-sm text-slate-600">No misses: every record of theirs was found.</p>;
  const kindLabel = (k) => RECORD_KINDS.find((x) => x.value === k)?.label?.replace(/s$/, '') || k;
  return (
    <div className="space-y-3">
      <ul className="flex flex-wrap gap-2" aria-label="Misses by cause">
        {summary.map((s) => (
          <li key={s.cause} className="px-3 py-1.5 rounded-lg border border-slate-300 text-sm">
            <span className="font-bold tabular-nums">{s.count}</span> {s.label}
          </li>
        ))}
      </ul>
      <div className="overflow-x-auto">
        <table className="w-full text-sm min-w-[560px]" aria-label="Misses">
          <thead>
            <tr className="text-left text-[11px] uppercase tracking-wider text-slate-500 border-b border-slate-200">
              <th className="py-1.5 pr-3 font-semibold">Their record</th>
              <th className="py-1.5 pr-3 font-semibold">Truck</th>
              <th className="py-1.5 pr-3 font-semibold">When</th>
              <th className="py-1.5 pr-3 font-semibold">Cause</th>
              <th className="py-1.5 font-semibold">Fix</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {misses.map((m, i) => (
              <tr key={m.record_id || i}>
                <td className="py-1.5 pr-3 text-slate-800">{kindLabel(m.kind)}</td>
                <td className="py-1.5 pr-3 font-mono text-xs">
                  {String(m.asset_key || '—').replace(/^reg:/, '')}
                </td>
                <td className="py-1.5 pr-3 tabular-nums text-slate-600">{fmtDateTime(m.at)}</td>
                <td className="py-1.5 pr-3 text-slate-900">{missCauseLabel(m.cause)}</td>
                <td className="py-1.5">
                  <FixLink href={m.fix_link} cause={m.cause} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export function DailyComparison({ series }) {
  if (!series.length)
    return (
      <p className="text-sm text-slate-600">
        Nothing to compare yet. Load their trip sheets, then run the comparison — each day appears
        here as it is matched.
      </p>
    );
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm min-w-[520px]" aria-label="Daily comparison">
        <thead>
          <tr className="text-left text-[11px] uppercase tracking-wider text-slate-500 border-b border-slate-200">
            <th className="py-1.5 pr-3 font-semibold">Day</th>
            <th className="py-1.5 pr-3 font-semibold text-right">Their trips</th>
            <th className="py-1.5 pr-3 font-semibold text-right">Found</th>
            <th className="py-1.5 pr-3 font-semibold text-right">Missed</th>
            <th className="py-1.5 pr-3 font-semibold text-right">Only ours</th>
            <th className="py-1.5 font-semibold">Found share</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {series.map((d) => (
            <tr key={d.date}>
              <td className="py-1.5 pr-3 tabular-nums text-slate-800">{d.date}</td>
              <td className="py-1.5 pr-3 tabular-nums text-right">{fmtNum(d.theirs)}</td>
              <td className="py-1.5 pr-3 tabular-nums text-right">{fmtNum(d.matched)}</td>
              <td
                className={`py-1.5 pr-3 tabular-nums text-right ${d.missed ? 'font-semibold text-slate-900' : 'text-slate-500'}`}
              >
                {fmtNum(d.missed)}
              </td>
              <td className="py-1.5 pr-3 tabular-nums text-right text-slate-500">
                {fmtNum(d.extra)}
              </td>
              <td className="py-1.5">
                <span className="flex items-center gap-2">
                  <span
                    className="h-2 w-28 rounded-full bg-slate-200 overflow-hidden"
                    aria-hidden="true"
                  >
                    <span
                      className="block h-full bg-slate-700"
                      style={{ width: `${Math.round((d.rate ?? 0) * 100)}%` }}
                    />
                  </span>
                  <span className="tabular-nums text-xs text-slate-700">{fmtRatio(d.rate)}</span>
                </span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function AccuracyReport({ report, floors, lanesById = {} }) {
  if (!report) {
    return <p className="text-sm text-slate-600">No report yet — run the comparison first.</p>;
  }
  return (
    <div className="space-y-4">
      <section className={`${cardClasses} p-4`}>
        <div className="flex flex-wrap items-baseline justify-between gap-2 mb-2">
          <h3 className="text-sm font-bold text-slate-900">Against the floors</h3>
          <p className="text-xs text-slate-500 tabular-nums">
            {fmtNum(report.assets)} trucks · {fmtNum(report.lanes)} lanes · computed{' '}
            {fmtDateTime(report.computed_at)}
          </p>
        </div>
        <MetricsTable report={report} floors={floors} />
      </section>
      <BreakdownTable
        title="Per truck"
        rows={report.per_asset}
        labelOf={(r) => String(r.asset_key).replace(/^reg:/, '')}
        floors={floors}
      />
      <BreakdownTable
        title="Per lane"
        rows={report.per_lane}
        labelOf={(r) => r.name || lanesById[r.lane_id] || r.lane_id}
        floors={floors}
      />
      <section className={`${cardClasses} p-4`} aria-label="Misses and how to fix them">
        <h3 className="text-sm font-bold text-slate-900 mb-2">Misses and how to fix them</h3>
        <MissesTable misses={report.misses} />
      </section>
    </div>
  );
}
